import { LocationStrategy } from '@angular/common';
import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, FormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { Checkbox } from 'primeng/checkbox';
import { Select } from 'primeng/select';
import { DatePicker } from 'primeng/datepicker';
import { Constants } from '../../_utility';
import { ActivatedRoute, Router } from '@angular/router';
import { BreadcrumbsService } from '../../_components/breadcrumbs/breadcrumbs.service';
import { SpinnerService } from '../../_components/spinner/spinner.service';
import { ApiService } from '../../_services';
import {
  CreateRestaurantSlotRequestBody,
  TableForSlot,
} from '../../_models/RestaurantSlot';
import { SlotService } from '../../_services/slot.service';
import { DateTime } from 'luxon';
import {
  checkIfBackInTime,
  checkIfEndTimeAfterStartTime,
  checkIfValidTime,
} from '../../_utility/validators';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { TableAvailability } from '../../_models/Restaurant';

@Component({
  selector: 'app-edit-slot',
  templateUrl: './edit-slot.component.html',
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [TableModule, Checkbox, Select, DatePicker, ButtonModule, ReactiveFormsModule, FormsModule, TranslateModule],
})
export class EditSlotComponent implements OnInit {
  form: FormGroup;
  tables: Array<TableForSlot> = [];
  selectedTables: TableForSlot[] = [];
  id_restaurant: string | undefined;
  id_restaurant_slot: string | undefined;
  edit: boolean = false;
  CATEGORIES = ['LUNCH', 'DINNER'];
  date: Date = new Date();
  dateParam?: string;
  constructor(
    private fb: FormBuilder,
    private location: LocationStrategy,
    private router: Router,
    private breadcrumb: BreadcrumbsService,
    private route: ActivatedRoute,
    private spinner: SpinnerService,
    private apiService: ApiService,
    private slotService: SlotService,
    private messageService: MessageService,
    private translate: TranslateService
  ) {
    this.form = this.fb.group(
      {
        date: ['', [Validators.required]],
        startTime: ['12:00', [Validators.required, checkIfValidTime()]],
        endTime: ['13:00', [Validators.required, checkIfValidTime()]],
        category: ['LUNCH', [Validators.required]],
      },
      { validators: [checkIfEndTimeAfterStartTime, checkIfBackInTime] }
    );
    this.form.valueChanges.subscribe((value) => {
      if (this.form.valid) {
        this.spinner.show();
        this.getTableAvailability().subscribe({
          next: (response) => {
            if (response) {
              this.onTableAvailabilityResult(response);
            }
          },
        });
      }
    });
  }

  ngOnInit() {
    setTimeout(() => {
      this.breadcrumb.setCrumbs(Constants.Routing.MODIFICA_SLOT.path);
    }, 200);
    this.spinner.show();
    this.route.queryParams.subscribe((param) => {
      if (param) {
        this.id_restaurant = param['restaurantId'];
        this.id_restaurant_slot = param['restaurantSlotId'];
        this.dateParam = param['date'];

        this.initForm();
      }
    });
  }

  initForm() {
    if (!this.id_restaurant && !this.id_restaurant_slot) {
      this.spinner.hide();
      this.router.navigate(Constants.Routing.GESTIONE_SLOT.routerLink);
    } else {
      if (this.id_restaurant_slot) {
        this.initEditForm();
      } else {
        this.initCreateForm();
      }
    }
  }

  initEditForm() {
    this.edit = true;
    this.slotService
      .getTablesSlot(this.id_restaurant!, this.id_restaurant_slot!, this.form)
      .pipe(
        switchMap((response) => {
          this.tables = response;
          this.tables.forEach((table) => {
            if (table.selected) {
              this.selectedTables.push(table);
            }
          });
          return this.form.valid ? this.getTableAvailability() : of(null); // Wait for this to complete
        })
      )
      .subscribe({
        next: (response) => {
          if (response) {
            this.onTableAvailabilityResult(response);
          }
          this.spinner.hide();
        },
        error: (error) => {
          this.spinner.hide();
          this.router.navigate(Constants.Routing.GESTIONE_SLOT.routerLink);
        },
      });
  }

  getTableAvailability() {
    const date = DateTime.fromJSDate(this.form.value.date).toSQLDate()!;
    const start_time = this.formatTime(this.form.value.startTime);
    const end_time = this.formatTime(this.form.value.endTime);
    return this.apiService.TableAvailability(
      this.id_restaurant!,
      date,
      start_time,
      end_time,
      this.id_restaurant_slot
    );
  }

  onTableAvailabilityResult(tableAvailability: TableAvailability[]) {
    if (tableAvailability) {
      this.tables.forEach((table) => {
        const availability = tableAvailability.find(
          (t) => t.id_table === table.fk_table
        );
        if (availability) {
          table.availability = availability.availability;
        }
      });
    }
    this.spinner.hide();
  }

  initCreateForm() {
    this.apiService.Restaurant(this.id_restaurant!).subscribe({
      next: (response) => {
        this.tables = response.tables.map((table) => {
          return {
            name: table.name,
            selected: false,
            myitalian: false,
            fk_table: table.id_table!,
            fk_status: table.fk_status!,
          };
        });
        if (this.dateParam) {
          this.form.controls['date'].setValue(
            DateTime.fromSQL(this.dateParam).toJSDate()
          );
        }

        this.spinner.hide();
      },
      error: (error) => {
        this.spinner.hide();
        this.router.navigate(Constants.Routing.GESTIONE_SLOT.routerLink);
      },
    });
    this.spinner.hide();
  }

  Back() {
    this.goTo('slot-management');
  }

  goTo(path: string) {
    switch (path) {
      case 'home': {
        this.router.navigate(Constants.Routing.HOME.routerLink);
        break;
      }
      case 'slot-management': {
        const queryParams: any = {
          restaurantId: this.id_restaurant,
        };
        if (this.dateParam) {
          queryParams['date'] = this.dateParam;
        } else if (this.form.value.date) {
          queryParams['date'] = DateTime.fromJSDate(
            this.form.value.date
          ).toSQLDate();
        }
        this.router.navigate(Constants.Routing.GESTIONE_SLOT.routerLink, {
          queryParams: queryParams,
        });
        break;
      }
    }
  }

  formatTime(time: string | Date): string {
    if (typeof time === 'string') {
      return time;
    }
    const hours = time.getHours().toString().padStart(2, '0');
    const minutes = time.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  onSubmit() {
    this.spinner.show();
    const payload: CreateRestaurantSlotRequestBody = {
      category: this.form.value.category,
      date: DateTime.fromJSDate(this.form.value.date).toSQLDate()!,
      start_time: this.formatTime(this.form.value.startTime),
      end_time: this.formatTime(this.form.value.endTime),
      tables: this.selectedTables.map((table) => ({
        id_table: table.fk_table,
        myitalian: table.myitalian,
        id_restaurant_slot_table: table.id_restaurant_slot_table,
      })),
    };

    if (this.edit) {
      payload.id_restaurant_slot = parseInt(this.id_restaurant_slot!);

      this.apiService
        .UpdateRestaurantSlot(
          this.id_restaurant!,
          this.id_restaurant_slot!,
          payload
        )
        .subscribe({
          next: () => {
            this.spinner.hide();
            this.messageService.add({
              severity: 'success',
              summary: this.translate.instant('messages.success_title'),
              detail: this.translate.instant('messages.slot_updated'),
              life: Constants.Notifications.LIFE,
              key: Constants.Notifications.SUCCESS_KEY,
            });
            this.goTo('slot-management');
          },
          error: () => {
            this.spinner.hide();
          },
        });
    } else {
      this.apiService
        .CreateRestaurantSlot(this.id_restaurant!, payload)
        .subscribe({
          next: () => {
            this.spinner.hide();
            this.messageService.add({
              severity: 'success',
              summary: this.translate.instant('messages.success_title'),
              detail: this.translate.instant('messages.slot_created'),
              life: Constants.Notifications.LIFE,
              key: Constants.Notifications.SUCCESS_KEY,
            });
            this.goTo('slot-management');
          },
          error: () => {
            this.spinner.hide();
          },
        });
    }
  }
}
